import React, { useEffect, useRef, useState } from 'react';
import { getBgColorByParty, getFontColorByParty, getStatusById } from './seatHelpers';
import CandidateIcons from './CandidateIcons';
import PartyArticlesPanel from './PartyArticlesPanel';
import { claimActive, releaseActive } from './activePartyPanel';

// interactive=false renders a display-only bar: no article-panel handlers (so
// clicks bubble to an enclosing overlay) and no PartyArticlesPanel mounted.
const CandidateBar = ({ candidate, totalVotes, interactive = true, showEnglishName = false }) => {
  const total = parseInt(totalVotes) || 0;
  const votes = parseInt(candidate.votes) || 0;
  const votePercent = total > 0 ? ((votes / total) * 100).toFixed(2) : 0;
  const isWinner = candidate.status == 1;
  const [panelQuery, setPanelQuery] = useState(null);
  // Render the bar at width 0 first, then arm the real width one frame later
  // so the CSS width transition plays the grow-in (same pattern as PollItem).
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setArmed(true))
    );
    return () => cancelAnimationFrame(raf);
  }, []);

  const closeFnRef = useRef(null);
  closeFnRef.current = () => setPanelQuery(null);

  useEffect(() => () => releaseActive(closeFnRef.current), []);

  const openPanel = () => {
    if (!candidate.party) return;
    claimActive(closeFnRef.current);
    setPanelQuery({ tag: candidate.party, fallbackTag: null });
  };

  // Clicking the candidate name searches that name first, falling back to the
  // party when the name has no related articles.
  const openNamePanel = () => {
    if (!candidate.candidate_name && !candidate.party) return;
    claimActive(closeFnRef.current);
    setPanelQuery({
      tag: candidate.candidate_name || candidate.party,
      fallbackTag: candidate.party,
    });
  };

  const handleClose = () => {
    releaseActive(closeFnRef.current);
    setPanelQuery(null);
  };

  return (
    <>
      <div className={`bar_chart_row ${isWinner ? 'winner' : ''}`}>
        {parseInt(candidate.status) > 0 && (
          <span className="winner_label">{getStatusById(candidate.status)}</span>
        )}
        <div
          className="bar_chart_label"
          {...(interactive && {
            role: 'button',
            tabIndex: 0,
            onClick: openPanel,
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPanel();
              }
            },
          })}
        >
          <span className="bcl-icon">
            <CandidateIcons candidate={candidate} />
          </span>
          <span className="bcl-party">
            {candidate.party} {candidate.alliance && `(${candidate.alliance})`}
          </span>
          <span className="bcl-photo">
            {candidate.candidate_logo && (
              <img
                className="cand-photo"
                src={candidate.candidate_logo}
                alt=""
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </span>
          <span
            className="bcl-name"
            {...(interactive && {
              role: 'button',
              tabIndex: 0,
              onClick: (e) => {
                e.stopPropagation();
                openNamePanel();
              },
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  openNamePanel();
                }
              },
            })}
          >
            {candidate.candidate_name}
            {showEnglishName && candidate.candidate_name_en ? (
              <span className="bcl-name-en">{candidate.candidate_name_en}</span>
            ) : null}
          </span>
        </div>
        <div className="bar_chart_bar_container">
          <div
            className="bar_chart_bar"
            style={{
              width: armed ? `${votePercent}%` : 0,
              backgroundColor: getBgColorByParty(candidate),
            }}
          >
            <span
              className="bar_chart_votes"
              style={{ color: getFontColorByParty(candidate) }}
            >
              {votes.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
      {interactive && (
        <PartyArticlesPanel
          tag={panelQuery?.tag}
          fallbackTag={panelQuery?.fallbackTag}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default CandidateBar;
